import { MapFiltersState } from "@/components/ListMapView";
import { makeAutoObservable, toJS } from "mobx";

export class MapFilterStore {
	private filters: MapFiltersState = [];

	constructor() {
		makeAutoObservable(this, {}, { autoBind: true });
	}

	setFilter(id: string, value: unknown) {
		const idx = this.filters.findIndex(f => f.id === id);
		if (value === undefined || value === null || value === "") {
			if (idx >= 0) this.filters.splice(idx, 1);
			return;
		}

		if (idx >= 0) {
			this.filters[idx] = { id, value };
		} else {
			this.filters.push({ id, value });
		}
	}

	setFilters(filters: MapFiltersState) {
		this.filters = filters;
	}

	getFilter(id: string): unknown {
		return toJS(this.filters.find(f => f.id === id)?.value);
	}

	getFilters(): MapFiltersState {
		return this.filters;
	}

	clear(id: string) {
		const idx = this.filters.findIndex(f => f.id === id);
		if (idx >= 0) this.filters.splice(idx, 1);
	}

	clearAll() {
		this.filters = [];
	}

	// ----- Multi-select helpers -----
	// Values for multi-select filters are stored as string[] under the filter id,
	// matching the shape list maps already use (e.g. structureTypeIds: string[]).
	// Ids are normalised to strings on read/write so callers can pass numbers or strings.

	isSelected(filterId: string, itemId: string | number): boolean {
		return this.asStringArray(filterId).includes(String(itemId));
	}

	toggle(filterId: string, itemId: string | number) {
		const idStr = String(itemId);
		const current = this.asStringArray(filterId);
		if (current.includes(idStr)) {
			const remaining = current.filter(id => id !== idStr);
			if (remaining.length === 0) this.clear(filterId);
			else this.setFilter(filterId, remaining);
		} else {
			this.setFilter(filterId, [...current, idStr]);
		}
	}

	selectedIds(filterId: string): Set<string> {
		return new Set(this.asStringArray(filterId));
	}

	selectAll(filterId: string, itemIds: ReadonlyArray<string | number>) {
		if (itemIds.length === 0) {
			this.clear(filterId);
			return;
		}
		this.setFilter(
			filterId,
			itemIds.map(id => String(id)),
		);
	}

	private asStringArray(filterId: string): string[] {
		const value = this.getFilter(filterId);
		if (!Array.isArray(value)) return [];
		return value.map(item => String(item));
	}

	get snapshot(): MapFiltersState {
		return toJS(this.filters);
	}

	get toJS(): MapFiltersState {
		return toJS(this.filters);
	}
}
